import { BaseCRMAdapter } from "../BaseCRMAdapter";
import { isGuid } from "@/services/generic.methods";
import { SmartHubLeadsAdapter } from "./SmartHubLeadsAdapter";
import { Get_Leads_Grid } from "@/modules/crm/leads/graphql-queries";

export class CRMLeadsAdapter extends BaseCRMAdapter {
  /**
   * @param {Object} params 
   * @param {Object} context 
   * @returns {Promise<Object>}
   */
  async getGridItems(params, context = {}) {
    if (typeof context.executeQuery === "function") {
      try {
        const response = await context.executeQuery(params);
        return response?.data;
      } catch (error) {
        // Fallback or rethrow as DataClientError if specific to this context
        throw error;
      }
    }

    return this._query({
      query: context.query,
      fetchPolicy: "no-cache",
      ...params
    });
  }

  /**
   * @param {Number|String} id 
   * @param {Object} context 
   * @returns {Promise<Object>}
   */
  async getLeadDetails(id, context = {}) {
    if (context.email) {
      const lead = await this.getLeadByEmail(context.email, {
        ...context,
        query: Get_Leads_Grid,
      });
      return { leadDetails: lead ? [lead] : [] };
    }
    return this._query({
      query: context.query,
      id: parseInt(id),
      fetchPolicy: "no-cache"
    });
  }

  async getLeadByEmail(email, context = {}) {
    const response = await this._query({
      query: context.query,
      where: { email: { eq: email } },
      take: 1,
      fetchPolicy: "no-cache"
    });
    return response?.leadsGrid?.items?.[0] || null;
  }

  async validateLeadField(name, value, context = {}) {
    if (typeof context.executeQuery === "function") {
      try {
        const payload = { [name]: value };
        if (name === "phone" && context.countryCode) {
            payload.countryCode = context.countryCode;
        }
        const response = await context.executeQuery(payload);
        return response?.data?.leadsGrid?.items?.[0] || null;
      } catch (error) {
        throw error;
      }
    }
    return null;
  }

  async createLead(params) {
    const { lead } = params;
    if (lead && lead.customFields) {
      params.customFields = lead.customFields;
      delete lead.customFields;
    }
    return this._bridgeMutation(params, null);
  }

  async updateLead(params) {
    const { lead } = params;
    if (lead && lead.customFields) {
      params.customFields = lead.customFields;
      delete lead.customFields;
    }
    return this._bridgeMutation(params, null);
  }

  async deleteLead(params) {
    return this._bridgeMutation(params, null);
  }

  async assignLead(params) {
    return this._bridgeMutation(params, null);
  }

  async changeLeadStatus(params) {
    return this._bridgeMutation(params, null);
  }

  async exportLeads(params, context = {}) {
    const { leadhub } = context;
    if (!leadhub) {
      throw new Error("leadhub is required in context for exporting leads in CRMLeadsAdapter");
    }
    
    // In legacy CRM, the export method name might be 'ExportLead' as expected by the server
    const { exportLead } = context.InvokeMethodNames || { exportLead: "ExportLead" };
    
    return leadhub.invoke(exportLead, params);
  }
}
